
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import {
  Loader,
  MealsCard,
  Pagination,
} from "../components/utils";

import {
  Section,
  Fotter,
  Header,
} from "../components/layout";

import { getAllCategorys } from "../redux/actions/category";
import { getFilteredMealsWithSort } from "../redux/actions/meals";

import MealsFilter from "../components/MealsFilter";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  FiGrid,
  FiList,
  FiColumns,
} from "react-icons/fi";

function AllResturentMeals() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(16);

  // Grid or list
  const [viewMode, setViewMode] = useState("grid");

  // Mobile grid columns
  const [mobileColumns, setMobileColumns] = useState("one");

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    categoryId: "",
    priceMin: "",
    priceMax: "",
    sortBy: "default",
  });

  // Redux State
  const {
    isLoading,
    FilltermealsWithSort,
    FilterpaginationWithSort,
  } = useSelector((state) => state.Meals);

  const { AllCategorys } = useSelector(
    (state) => state.Categorys
  );

  // Fetch meals when filters or pagination change
  useEffect(() => {
    dispatch(
      getFilteredMealsWithSort(
        page,
        limit,
        filters
      )
    );
  }, [page, limit, filters, dispatch]);

  // Fetch categories
  useEffect(() => {
    dispatch(getAllCategorys());
  }, [dispatch]);

  const totalPages =
    FilterpaginationWithSort?.totalPages || 0;

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleMobileOneColumn = () => {
    setViewMode("grid");
    setMobileColumns("one");
  };

  const handleMobileTwoColumns = () => {
    setViewMode("grid");
    setMobileColumns("two");
  };

  const handleDesktopGrid = () => {
    setViewMode("grid");
  };

  const handleListView = () => {
    setViewMode("list");
  };

  const containerVariants = {
    hidden: {
      opacity: 0,
    },

    visible: {
      opacity: 1,

      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: {
      y: 40,
      opacity: 0,
    },

    visible: {
      y: 0,
      opacity: 1,

      transition: {
        type: "spring",
        stiffness: 100,
        damping: 14,
      },
    },
  };

  // Local sorting fallback
  const getSortedMeals = () => {
    if (!FilltermealsWithSort) {
      return [];
    }

    const sorted = [...FilltermealsWithSort];

    switch (filters.sortBy) {
      case "price_asc":
        sorted.sort(
          (a, b) =>
            (a.price || 0) - (b.price || 0)
        );
        break;

      case "price_desc":
        sorted.sort(
          (a, b) =>
            (b.price || 0) - (a.price || 0)
        );
        break;

      default:
        sorted.sort(
          (a, b) =>
            new Date(b.createdAt) -
            new Date(a.createdAt)
        );
    }

    return sorted;
  };

  const displayedMeals = getSortedMeals();

  const compactCard =
    viewMode === "grid" &&
    mobileColumns === "two";

  return (
    <>
      <Header
        active={5}
        DropActive={5}
      />

      {/* Hero Section */}
      <motion.section
        className="
          relative
          overflow-hidden
          pt-28
          pb-16
          mb-8
          bg-gradient-to-br
          from-color-1/10
          via-back-primery
          to-color-3/10
          dark:from-color-1/5
          dark:via-back-darkprimery
          dark:to-color-3/5
        "
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div
          className="
            absolute
            top-0
            right-0
            w-80
            h-80
            rounded-full
            bg-color-1/10
            blur-3xl
          "
        />

        <div
          className="
            absolute
            bottom-0
            left-0
            w-80
            h-80
            rounded-full
            bg-color-3/10
            blur-3xl
          "
        />

        <div className="container relative z-10 text-center">
          <motion.h1
            className="
              text-4xl
              md:text-5xl
              lg:text-6xl
              font-kufi
              font-bold
              mb-4
              pb-6
              bg-gradient-to-r
              from-color-1
              to-color-2
              dark:to-color-3
              bg-clip-text
              text-transparent
            "
            initial={{
              y: -40,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            transition={{ delay: 0.2 }}
          >
            {t("all meals")}
          </motion.h1>

          <motion.p
            className="
              body-1
              text-n-6
              dark:text-n-3
              max-w-2xl
              mx-auto
            "
            initial={{
              y: -20,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            transition={{ delay: 0.3 }}
          >
            {t(
              "discover delicious meals from our menu"
            )}
          </motion.p>
        </div>
      </motion.section>

      {isLoading ? (
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <Section>
          <div
            id="meals-section"
            className="container py-12 lg:py-20"
          >
            {/* Filters — unchanged */}
            <MealsFilter
              onFilterChange={handleFilterChange}
              categories={AllCategorys}
              initialFilters={filters}
            />

            {/* View Toggle & Stats */}
            <motion.div
              className="
                flex
                flex-col
                sm:flex-row
                sm:items-center
                justify-between
                gap-4
                mb-12
                p-4
                md:p-5
                rounded-2xl
                border
                border-color-1/10
                bg-white/80
                dark:bg-back-darksecondary/80
                backdrop-blur-xl
                shadow-lg
              "
              initial={{
                opacity: 0,
                y: -20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.5,
                delay: 0.1,
              }}
            >
              <div className="flex items-center justify-between sm:justify-start gap-3">
                {/* Result Count */}
                <span className="text-n-5 dark:text-n-3 body-2 whitespace-nowrap">
                  {t("showing")}{" "}
                  <span className="font-bold text-color-1">
                    {displayedMeals?.length || 0}
                  </span>{" "}
                  {t("items")}
                </span>

                <div className="hidden sm:block h-6 w-px bg-gray-300 dark:bg-n-6" />

                {/* View Buttons */}
                <div
                  className="
                    flex
                    items-center
                    gap-1.5
                    p-1.5
                    rounded-xl
                    bg-back-secondary
                    dark:bg-back-darktertiary
                  "
                >
                  {/* Mobile one column */}
                  <button
                    type="button"
                    onClick={handleMobileOneColumn}
                    className={`
                      md:hidden
                      w-10
                      h-10
                      rounded-lg
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid" &&
                        mobileColumns === "one"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-n-6 dark:text-n-3 hover:bg-color-1/10 hover:text-color-1"
                      }
                    `}
                    aria-label={t("one column view")}
                    title={t("one column view")}
                  >
                    <FiList size={18} />
                  </button>

                  {/* Mobile two columns */}
                  <button
                    type="button"
                    onClick={handleMobileTwoColumns}
                    className={`
                      md:hidden
                      w-10
                      h-10
                      rounded-lg
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid" &&
                        mobileColumns === "two"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-n-6 dark:text-n-3 hover:bg-color-1/10 hover:text-color-1"
                      }
                    `}
                    aria-label={t("two columns view")}
                    title={t("two columns view")}
                  >
                    <FiColumns size={18} />
                  </button>

                  {/* Tablet and desktop grid */}
                  <button
                    type="button"
                    onClick={handleDesktopGrid}
                    className={`
                      hidden
                      md:flex
                      w-10
                      h-10
                      rounded-lg
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "grid"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-n-6 dark:text-n-3 hover:bg-color-1/10 hover:text-color-1"
                      }
                    `}
                    aria-label={t("grid view")}
                    title={t("grid view")}
                  >
                    <FiGrid size={18} />
                  </button>

                  {/* List */}
                  <button
                    type="button"
                    onClick={handleListView}
                    className={`
                      w-10
                      h-10
                      rounded-lg
                      flex
                      items-center
                      justify-center
                      transition-all
                      duration-300
                      ${
                        viewMode === "list"
                          ? "bg-color-1 text-white shadow-lg"
                          : "text-n-6 dark:text-n-3 hover:bg-color-1/10 hover:text-color-1"
                      }
                    `}
                    aria-label={t("list view")}
                    title={t("list view")}
                  >
                    <FiList size={18} />
                  </button>
                </div>
              </div>

              {/* Total */}
              <div className="text-sm text-n-5 dark:text-n-4 font-kufi">
                {FilterpaginationWithSort?.totalItems >
                  0 && (
                  <span>
                    {t("total")}:{" "}
                    <strong className="text-color-1">
                      {
                        FilterpaginationWithSort.totalItems
                      }
                    </strong>{" "}
                    {t("meals")}
                  </span>
                )}
              </div>
            </motion.div>

            {/* Meals Grid/List */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`${viewMode}-${mobileColumns}-${filters.sortBy}`}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0 }}
                className={
                  viewMode === "grid"
                    ? `
                      grid
                      ${
                        mobileColumns === "two"
                          ? "grid-cols-2 gap-3"
                          : "grid-cols-1 gap-6"
                      }
                      md:grid-cols-2
                      md:gap-7
                      lg:grid-cols-3
                      lg:gap-8
                    `
                    : "flex flex-col gap-6"
                }
              >
                {displayedMeals?.map((meal) => (
                  <motion.div
                    key={meal._id}
                    variants={itemVariants}
                    layout
                    className="min-w-0"
                  >
                    <MealsCard
                      meals={meal}
                      viewMode={viewMode}
                      compact={compactCard}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Empty State */}
            {(!displayedMeals ||
              displayedMeals.length === 0) && (
              <motion.div
                className="text-center py-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="text-6xl mb-4">
                  🍽️
                </div>

                <h3 className="h4 text-n-6 dark:text-n-3 mb-2">
                  {t("no dishes found")}
                </h3>

                <p className="body-2 text-n-5 dark:text-n-4">
                  {t("try adjusting your filters")}
                </p>

                <button
                  type="button"
                  onClick={() =>
                    handleFilterChange({
                      search: "",
                      categoryId: "",
                      priceMin: "",
                      priceMax: "",
                      sortBy: "default",
                    })
                  }
                  className="
                    mt-6
                    px-6
                    py-3
                    bg-color-1
                    text-white
                    rounded-xl
                    font-kufi
                    shadow-lg
                    transition-all
                    duration-300
                    hover:bg-color-3
                    hover:-translate-y-1
                  "
                >
                  {t("clear all filters")}
                </button>
              </motion.div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                className="mt-16"
                initial={{
                  opacity: 0,
                  y: 30,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{ delay: 0.4 }}
              >
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  setPage={setPage}
                />
              </motion.div>
            )}
          </div>
        </Section>
      )}

      <Fotter />
    </>
  );
}

export default AllResturentMeals;
